"use client"

import { MultiChainMintCheckout } from "@/app/components/multi-chain-mint/multi-chain-mint-checkout"
import { QuantityPicker } from "@/app/drop/actions/quantity-picker"
import { MintSuccessPopup } from "@/app/drop/order/mint-success-popup"
import { DropMoleculeReatom } from "@/app/drop/store/drop-store.reatom"
import { PriceWrapper } from "@/app/price"
import { useEvmMint } from "@/app/sdk/action-molecules/evm/evm-mint-molecule"
import { useWalletConnect } from "@/app/wallet-connect/hooks"

import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { MinimalDrop } from "@anaconda/core/drop/types"
import { Trans, useLingui } from "@lingui/react/macro"
import {
  Button,
  type ButtonProps,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { Suspense, useCallback, useMemo, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useConnectStatus } from "../../nft/order/widget/actions/use-connect-status"
import { calculateAvailableToMint } from "./utils"

type Props = Omit<ButtonProps, "onClick" | "disabled"> & {
  withPrice?: boolean
  withQuantityPicker?: boolean
  drop: MinimalDrop
}

const MAX_QUANTITY = 50

export function MintWidget(props: Props) {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<MintWidgetSkeleton />}>
        <MintWidgetContent {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

const MintWidgetContent = reatomComponent<Props>(
  ({ drop, withPrice = true, withQuantityPicker = true, ...rest }) => {
    console.log("🎨 [MintWidgetContent] Rendering for drop:", drop.id)

    const { t } = useLingui()
    const [quantity, setQuantity] = useState(1)
    const [openMultiChainMint, setOpenMultiChainMint] = useState(false)
    const [successPopup, setSuccessPopup] = useState<{
      open: boolean
      transactionId: string
      mintedCount: number
    } | null>(null)

    const handleMintSuccess = useCallback(
      (result: { txId: string; drop: MinimalDrop; quantity?: number }) => {
        setSuccessPopup({
          open: true,
          transactionId: result.txId,
          mintedCount: result.quantity ?? 1,
        })
      },
      [],
    )

    const {
      currentPhaseAtom,
      mintedCountAtom,
      isThirdwebDropAtom,
      isMultiChainDropAtom,
      userClaimLimitInfoAtom,
      myMintsPerPhaseAtom,
      isMintDisabledForUserAtom,
      isMintDisabledAtom,
      mintNotAvailableMessageAtom,
      dropDisplayPriceAtom,
    } = useMolecule(DropMoleculeReatom)

    // NOTE: Effects are activated by DropEffectsActivator at the layout level
    // Do NOT activate them here to avoid multiple instances

    const { isConnected } = useWalletConnect()

    const currentPhase = currentPhaseAtom()
    const mintedCount = mintedCountAtom()
    const userMintedCountInCurrentPhase = myMintsPerPhaseAtom() ?? 0
    const userClaimLimitInfo = userClaimLimitInfoAtom()
    const isMintDisabledForUser = isMintDisabledForUserAtom()
    const isMintDisabled = isMintDisabledAtom()
    const mintNotAvailableMessage = mintNotAvailableMessageAtom()

    const isThirdwebDrop = isThirdwebDropAtom()
    const isMultiChainDrop = isMultiChainDropAtom()

    const disabled = isConnected
      ? isMintDisabled || isMintDisabledForUser
      : isMintDisabled

    const { mutate, isPending } = useEvmMint(drop, {
      onMintSuccess: handleMintSuccess,
    })

    const promptWalletConnection = useConnectStatus(() => {
      if (isThirdwebDrop) {
        ;(mutate as (variables: { quantity?: number }) => void)({ quantity })
      } else if (isMultiChainDrop) {
        setOpenMultiChainMint(true)
      } else {
        mutate(undefined)
      }
    })

    const availableToMint = calculateAvailableToMint({
      maxMintPerWallet: currentPhase.maxMintPerWallet,
      phaseLimit: currentPhase.maxClaimableSupply,
      totalSupply: drop.totalSupply,
      mintedCount,
      userMintedCount: userMintedCountInCurrentPhase,
      maxQuantity: MAX_QUANTITY,
      phaseType: currentPhase.type,
      userClaimLimitInfo,
    })

    const mint = useMemo(() => {
      if (!isConnected) return promptWalletConnection
      if (isThirdwebDrop) {
        return () => {
          ;(mutate as (variables: { quantity?: number }) => void)({ quantity })
        }
      }
      if (isMultiChainDrop) {
        return () => {
          setOpenMultiChainMint(true)
        }
      }
      return () => {
        mutate(undefined)
      }
    }, [
      isConnected,
      promptWalletConnection,
      mutate,
      quantity,
      isThirdwebDrop,
      isMultiChainDrop,
    ])

    const handleMultiChainMint = (blockchain: Blockchain) => {
      const mintFn = mutate as unknown as (variables: {
        quantity?: number
        blockchain?: Blockchain
      }) => void
      mintFn({ blockchain, quantity })
      setOpenMultiChainMint(false)
    }

    return (
      <div className={cn("flex items-center gap-2", rest.className)}>
        {/* Only the Thirdweb drops support the quantity parameter */}
        {isThirdwebDrop && withQuantityPicker && (
          <QuantityPicker
            defaultValue={1}
            onChange={setQuantity}
            className="w-20 rounded-md border-foreground/10 bg-foregroundInvert/20 backdrop-blur-sm"
            maxValue={availableToMint}
            disabled={isConnected ? disabled : isMintDisabled}
            dataTestId="mint-quantity-picker"
          />
        )}
        <Tooltip>
          <TooltipTrigger
            className={cn(disabled && "cursor-not-allowed")}
            asChild
          >
            <Button
              loading={isPending}
              disabled={disabled}
              onClick={mint}
              {...rest}
              className={cn(
                "mint-button",
                "disabled:pointer-events-auto disabled:hover:transform-none",
              )}
              data-testid="mint-button"
            >
              {withPrice ? (
                <MintText drop={drop} quantity={quantity} />
              ) : (
                <ShortMintText />
              )}
            </Button>
          </TooltipTrigger>
          {mintNotAvailableMessage && isConnected && (
            <TooltipContent>{t(mintNotAvailableMessage)}</TooltipContent>
          )}
        </Tooltip>
        <Suspense fallback={null}>
          <MultiChainMintCheckout
            open={openMultiChainMint}
            onOpenChange={setOpenMultiChainMint}
            drop={drop}
            onMint={handleMultiChainMint}
          />
        </Suspense>
        <Suspense fallback={null}>
          <MintSuccessPopup
            drop={drop}
            open={successPopup?.open ?? false}
            transactionId={successPopup?.transactionId ?? ""}
            mintedCount={successPopup?.mintedCount ?? 1}
            onClose={() => setSuccessPopup(null)}
          />
        </Suspense>
      </div>
    )
  },
  "MintWidgetContent",
)

export function ShortMintText() {
  return (
    <>
      <Trans>Mint</Trans>
    </>
  )
}

export const MintText = reatomComponent(
  ({ drop, quantity }: { drop: MinimalDrop; quantity: number }) => {
    const { dropDisplayPriceAtom } = useMolecule(DropMoleculeReatom)
    const displayPrice = dropDisplayPriceAtom()

    const amount =
      displayPrice.amount !== "0" ? Number(displayPrice.amount) * quantity : "0"

    return (
      <>
        {amount !== "0" ? (
          <>
            <Trans>Mint for</Trans>{" "}
            <PriceWrapper
              currency={displayPrice.currency}
              amount={amount.toString()}
              className="[&_.currency-symbol]:text-foregroundInvert/100"
            />
          </>
        ) : (
          <Trans>Free mint</Trans>
        )}
      </>
    )
  },
  "MintText",
)

export function MintWidgetSkeleton() {
  return (
    <div className="flex w-full items-center gap-2">
      <Skeleton className="h-[34px] w-20" />
      <Skeleton className="h-[34px] w-full" />
    </div>
  )
}
